import { memo, useCallback, useState } from "react";
import { Textarea } from "@/components/ui/textarea";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

const PREVIEW_CLASS =
  "text-sm text-left w-full cursor-pointer p-2 rounded border-0 bg-transparent hover:bg-muted/50 focus:bg-muted/50 outline-none line-clamp-2";

interface CommentCellWithPopoverProps {
  id: string;
  value: string;
  isDraft?: boolean;
  onDraftCommentChange?: (id: string, value: string) => void;
  placeholder?: string;
}

function CommentCellWithPopover({
  id,
  value,
  isDraft = false,
  onDraftCommentChange,
  placeholder,
}: CommentCellWithPopoverProps) {
  const [open, setOpen] = useState(false);
  const handleClick = useCallback((e: React.SyntheticEvent) => {
    e.stopPropagation();
  }, []);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    e.stopPropagation();
  }, []);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onDraftCommentChange?.(id, e.target.value);
    },
    [id, onDraftCommentChange],
  );

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    e.stopPropagation();
    if (e.key === "Escape") {
      setOpen(false);
    }
  }, []);

  const handleCloseAutoFocus = useCallback((e: Event) => {
    e.preventDefault();
  }, []);

  const preview = value.trim() || (isDraft ? placeholder : "");

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(PREVIEW_CLASS, "min-h-[2.5rem]")}
          onClick={handleClick}
        >
          <span className="block text-left font-normal break-words">
            {preview}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="min-w-[320px] w-auto max-w-[90vw] p-0"
        align="start"
        sideOffset={6}
        onClick={handleClick}
        onPointerDown={handlePointerDown}
        onCloseAutoFocus={handleCloseAutoFocus}
      >
        <Textarea
          readOnly={!isDraft}
          value={value}
          onChange={handleChange}
          placeholder={isDraft ? placeholder : undefined}
          rows={6}
          className={cn(
            "min-h-[120px] resize-y rounded border-0 shadow-none focus-visible:ring-0",
            !isDraft && "cursor-default bg-transparent",
          )}
          onClick={handleClick}
          onPointerDown={handlePointerDown}
          onKeyDown={handleKeyDown}
        />
      </PopoverContent>
    </Popover>
  );
}

export default memo(CommentCellWithPopover);
